/**
 * BatchQuantityCorrectionModal (S6.2) — the 改數量 low-privilege correction
 * loop for outsourced loaders.
 *
 * "Loaded qty fixed at seal" stays the rule; this modal is the SANCTIONED
 * exception path: sealed batches only (the action only renders there —
 * hide-don't-disable), every correction carries a mandatory business reason
 * (shared `ReasonRequiredModal`), and the server appends the before→after
 * change set to the batch correction log while the OCC short-load facts
 * re-derive from the corrected quantities.
 */

import { useEffect, useState } from 'react';
import { App, InputNumber, Table, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { extractApiError } from '@/hooks/useSalesOrders';
import type { ShipmentBatch } from '@/types/api/shipping';
import { useBatchCaptureMutations } from '../useBatchCapture';

const { Text } = Typography;

interface CorrectionLine {
  id: string;
  sku: string;
  qtyPlanned: number;
  /** The sealed (or previously corrected) loaded quantity. */
  qtyCurrent: number;
  /** The proposed corrected quantity. */
  qtyCorrected: number;
}

export interface BatchQuantityCorrectionModalProps {
  open: boolean;
  /** The sealed batch being corrected (null while closed). */
  batch: ShipmentBatch | null;
  onClose: () => void;
  /** Called after a successful correction (page refreshes its list). */
  onDone: () => void;
}

const BatchQuantityCorrectionModal = ({
  open,
  batch,
  onClose,
  onDone,
}: BatchQuantityCorrectionModalProps) => {
  const { t } = useTranslation(['shipping', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();

  const { correctBatchQuantities } = useBatchCaptureMutations();
  const [lines, setLines] = useState<CorrectionLine[]>([]);

  useEffect(() => {
    setLines(
      (batch?.lines ?? []).map((line) => ({
        id: line.id,
        sku: line.sku,
        qtyPlanned: line.qtyPlanned,
        qtyCurrent: line.qtyLoaded,
        qtyCorrected: line.qtyLoaded,
      }))
    );
  }, [batch, open]);

  const changedCount = lines.filter((line) => line.qtyCorrected !== line.qtyCurrent).length;

  const handleSubmit = async (reason: string): Promise<boolean | void> => {
    if (!batch) return false;
    const changed = lines.filter((line) => line.qtyCorrected !== line.qtyCurrent);
    if (!changed.length) {
      message.warning(t('shipping:batch.correction.validation.noChange'));
      return false; // keep the modal open — nothing to submit
    }
    try {
      await correctBatchQuantities(batch.id, {
        reason,
        lines: changed.map((line) => ({ id: line.id, qtyLoaded: line.qtyCorrected })),
      });
      message.success(t('shipping:batch.correction.messages.corrected'));
      onClose();
      onDone();
    } catch (error) {
      const body = extractApiError(error);
      message.error(body.message || t('common:messages.updateFailed'));
      return false;
    }
  };

  return (
    <ReasonRequiredModal
      open={open}
      title={t('shipping:batch.correction.title', { batchNumber: batch?.batchNumber ?? '' })}
      description={t('shipping:batch.correction.description')}
      submitText={t('shipping:batch.correction.submit')}
      cancelText={t('common:actions.cancel')}
      reasonLabel={t('shipping:batch.correction.reasonLabel')}
      reasonPlaceholder={t('shipping:batch.correction.reasonPlaceholder')}
      requiredMessage={t('shipping:batch.correction.validation.reasonRequired')}
      width={640}
      onSubmit={handleSubmit}
      onCancel={onClose}
    >
      <Table<CorrectionLine>
        size="small"
        rowKey="id"
        dataSource={lines}
        pagination={false}
        style={{ marginBottom: '16px' }}
        columns={[
          {
            title: t('shipping:notice.modal.sku'),
            dataIndex: 'sku',
            key: 'sku',
            render: (value: string) => (
              <span style={{ fontWeight: token.fontWeightStrong }}>{value}</span>
            ),
          },
          {
            title: t('shipping:batch.cutOffModal.qtyPlanned'),
            dataIndex: 'qtyPlanned',
            key: 'qtyPlanned',
            align: 'right',
            width: 90,
            render: (value: number) => (
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
            ),
          },
          {
            title: t('shipping:batch.correction.current'),
            dataIndex: 'qtyCurrent',
            key: 'qtyCurrent',
            align: 'right',
            width: 110,
            render: (value: number) => (
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
            ),
          },
          {
            title: t('shipping:batch.correction.corrected'),
            key: 'qtyCorrected',
            width: 130,
            render: (_: unknown, line) => (
              <InputNumber
                min={0}
                max={line.qtyPlanned}
                value={line.qtyCorrected}
                size="large"
                style={{ width: '100%' }}
                aria-label={t('shipping:batch.correction.correctedFor', { sku: line.sku })}
                onChange={(value) =>
                  setLines((prev) =>
                    prev.map((candidate) =>
                      candidate.id === line.id
                        ? { ...candidate, qtyCorrected: value ?? 0 }
                        : candidate
                    )
                  )
                }
              />
            ),
          },
        ]}
      />
      {changedCount > 0 && (
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: '12px' }}
        >
          {t('shipping:batch.correction.changedHint', { count: changedCount })}
        </Text>
      )}
    </ReasonRequiredModal>
  );
};

export default BatchQuantityCorrectionModal;
